import { differenceInDays, format, parseISO, subDays } from 'date-fns'

import type { CheckValidationQueueItem } from '@/Network/Types/Responses/CheckValidationResponses'
import { formatDateInOffset, formatHourMinuteInOffset } from '@/utils/date'
import type { CheckValidationPeriodDays } from '@/constants/check-validation.constants'

import type { CheckValidationRow } from './types'

export const DISPLAY_FALLBACK = '—'

/** Sinal de menos tipográfico (U+2212), como no protótipo. */
const MINUS_SIGN = '−'
const MS_PER_MINUTE = 60_000

/** `CRM 12345 · SP`; `null` quando o CRM não existe no cadastro do médico. */
export const formatCrmLabel = (
  crm: string | null | undefined,
  crmUf: string | null | undefined
): string | null => {
  if (!crm) return null
  return crmUf ? `CRM ${crm} · ${crmUf}` : `CRM ${crm}`
}

/**
 * Hora compacta do protótipo: `07h` quando os minutos são zero, `07h12` caso
 * contrário. Lê o instante no fuso do plantão (offset ISO) — ver `utils/date.ts`
 * sobre por que não usar a opção `timeZone` do `Intl`.
 */
export const formatHourCompact = (iso: string | null | undefined, timezone: string): string => {
  if (!iso) return DISPLAY_FALLBACK
  const hourMinute = formatHourMinuteInOffset(iso, timezone)
  if (!hourMinute) return DISPLAY_FALLBACK
  const [hours, minutes] = hourMinute.split(':')
  return minutes === '00' ? `${hours}h` : `${hours}h${minutes}`
}

/** `07h–19h`; `—` quando qualquer ponta está ausente. */
export const formatHourRange = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timezone: string
): string => {
  if (!startIso || !endIso) return DISPLAY_FALLBACK
  const start = formatHourCompact(startIso, timezone)
  const end = formatHourCompact(endIso, timezone)
  if (start === DISPLAY_FALLBACK || end === DISPLAY_FALLBACK) return DISPLAY_FALLBACK
  return `${start}–${end}`
}

const toMinutes = (iso: string): number | null => {
  const ms = new Date(iso).getTime()
  return Number.isNaN(ms) ? null : ms / MS_PER_MINUTE
}

/**
 * "Dif." do protótipo: soma das divergências de ENTRADA e SAÍDA em minutos,
 * cada uma com sinal (`digitado − previsto`). Positivo = registrou depois do
 * previsto; negativo = antes.
 *
 *   previsto 07h–19h, digitado 07h12–19h05 → (+12) + (+5) = +17
 *   previsto 19h–07h, digitado 19h00–06h30 → (0) + (−30) = −30
 *
 * Não é a diferença de duração (esses exemplos dariam −7 e −30): a métrica
 * escolhida expõe as duas pontas, que é o que o auditor confere na evidência.
 * `null` quando falta qualquer instante ou algum é inválido.
 */
export const computeDiffMinutes = (
  expected: { start: string | null | undefined; end: string | null | undefined },
  typed: { checkin: string | null | undefined; checkout: string | null | undefined }
): number | null => {
  if (!expected.start || !expected.end || !typed.checkin || !typed.checkout) return null
  const start = toMinutes(expected.start)
  const end = toMinutes(expected.end)
  const checkin = toMinutes(typed.checkin)
  const checkout = toMinutes(typed.checkout)
  if (start === null || end === null || checkin === null || checkout === null) return null
  return Math.round(checkin - start + (checkout - end))
}

/** `+17 min` / `−30 min` / `0 min`; `—` para `null`. */
export const formatDiffMinutes = (diff: number | null): string => {
  if (diff === null) return DISPLAY_FALLBACK
  if (diff > 0) return `+${diff} min`
  if (diff < 0) return `${MINUS_SIGN}${Math.abs(diff)} min`
  return '0 min'
}

/**
 * Tempo em fila desde o envio para análise: `hoje` / `1 dia` / `n dias`.
 * `now` é parâmetro para o teste ser determinístico.
 */
export const formatQueueTime = (
  submittedAtIso: string | null | undefined,
  now: Date = new Date()
): string => {
  if (!submittedAtIso) return DISPLAY_FALLBACK
  const submittedAt = parseISO(submittedAtIso)
  if (Number.isNaN(submittedAt.getTime())) return DISPLAY_FALLBACK
  const days = differenceInDays(now, submittedAt)
  if (days <= 0) return 'hoje'
  return days === 1 ? '1 dia' : `${days} dias`
}

/**
 * Preset "Últimos N dias" → `{ startDate, endDate }` em `yyyy-MM-dd` no relógio
 * LOCAL (não `toISOString()`, que em UTC pode cair no dia seguinte à noite).
 * O backend recorta pela data do plantão.
 */
export const periodPresetToRange = (
  days: CheckValidationPeriodDays,
  now: Date = new Date()
): { startDate: string; endDate: string } => ({
  startDate: format(subDays(now, days), 'yyyy-MM-dd'),
  endDate: format(now, 'yyyy-MM-dd')
})

export const mapQueueItemToRow = (
  item: CheckValidationQueueItem,
  now: Date = new Date()
): CheckValidationRow => {
  // Plantão sem fuso na fila: string vazia cai no fuso padrão do produto
  // (Brasília), que mora em `src/utils/date.ts`. Mesmo padrão do PostStation.
  const timezone = item.station.timezone || ''
  const diffMinutes = computeDiffMinutes(
    { start: item.station.startHour, end: item.station.endHour },
    { checkin: item.check?.checkin, checkout: item.check?.checkout }
  )

  return {
    checkId: item.checkId,
    stationId: item.station.id,
    scheduleId: item.schedule.id,
    status: item.validationStatus,
    version: item.validationVersion,
    submittedAtIso: item.validationSubmittedAt,
    isOverdue: item.isOverdue,
    isEditedSinceSubmit: item.validationEditedSinceSubmit ?? false,
    doctor: {
      id: item.doctor?.id ?? 0,
      name: item.doctor?.name ?? null,
      crmLabel: formatCrmLabel(item.doctor?.crm, item.doctor?.crmUf)
    },
    unit: item.healthUnit?.name ?? DISPLAY_FALLBACK,
    scheduleLabel: item.schedule.name ?? DISPLAY_FALLBACK,
    // A data do plantão sai do início previsto lido no fuso do plantão — a fila
    // não devolve `station.date`, e o instante de início já a determina.
    stationDate: formatDateInOffset(item.station.startHour, timezone) ?? DISPLAY_FALLBACK,
    expectedStartIso: item.station.startHour,
    expectedEndIso: item.station.endHour,
    expectedRange: formatHourRange(item.station.startHour, item.station.endHour, timezone),
    typedRange: formatHourRange(item.check?.checkin, item.check?.checkout, timezone),
    diffMinutes,
    diffLabel: formatDiffMinutes(diffMinutes),
    evidenceCount: item.evidenceCount ?? null,
    queueTimeLabel: formatQueueTime(item.validationSubmittedAt, now),
    createdBy: item.createdBy
  }
}
